import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment-timezone';
import Time from 'lib/time';

import TextField from '@material-ui/core/TextField';
import Select from '@material-ui/core/Select';
import Button from '@material-ui/core/Button';
import MenuItem from '@material-ui/core/MenuItem';

import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormGroup from '@material-ui/core/FormGroup';
import InputLabel from '@material-ui/core/InputLabel';
import DialogTitle from '@material-ui/core/DialogTitle';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';

const timespanPickerStyles = makeStyles((theme) => ({
  formControl: {
    margin: theme.spacing(1),
  },
  inlineFormControl: {
    width: 250,
    marginRight: theme.shape.padding
  },
  quickSelectItem: {
    textTransform: 'capitalize',
    backgroundColor: theme.palette.background.paper
  },
  presetContainer: {
    display: 'flex',
    cursor: 'pointer',
    alignItems: 'center'
  },
  inputContainer: {
    display: 'flex',
    marginRight: 10,
    flexDirection: 'column',
    alignItems: 'flex-start'
  },
  labelText: {
    fontSize: 14,
    marginBottom: 5
  },
  textContainer: {
    minWidth: 250,
    display: 'flex',
    borderRadius: 10,
    alignItems: 'center',
    padding: '10px 15px',
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    '& p': {
      fontSize: 15,
      marginBottom: 0
    }
  },
  newSearchField: {
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper
  },
  customSelectOption: {
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  errorMessage: {
    margin: 0,
    paddingLeft: 20,
    color: theme.palette.error.main
  }
}));



const defaultDeltas = ["@:-10","@-1","@-4","@-24","-7:d","-30:d","-1:w","0:m", "-1:m", "0:q", "-1:q"];

export const TimespanPicker = (props) => {
  const deltas = props.deltas === undefined ? defaultDeltas.map(d=>new Time(d)) : props.deltas.map(d=>new Time(d))
  
  const [selectedDelta, setSelectedDelta] = useState("-30:d")
  const [customDateRange, setCustomDateRange] = useState(false)
  const [startTime, setStartTime] = useState(props.startTime)
  const [endTime, setEndTime] = useState(props.endTime)
  const [errorMessage, setErrorMessage] = useState(null)

  const classes = timespanPickerStyles();

  useEffect(() => {
    const dMatch = deltas.find(d=>{
      const ranges = d.getTimeRange()
      return ranges.startTime.diff(props.startTime || startTime, 'minutes')===0 && ranges.endTime.diff(props.endTime || endTime, 'minutes')===0
    })

    if (dMatch){
      const ranges = dMatch.getTimeRange()
      setStartTime(moment.utc(ranges.startTime))
      setEndTime(moment.utc(ranges.endTime))
      setSelectedDelta(dMatch.timeString)
    } else {
      setStartTime(moment.utc(props.startTime))
      setEndTime(moment.utc(props.endTime))
      setSelectedDelta(props.selectedDelta || 'custom')
    }
  }, [props.startTime, props.endTime, props.selectedDelta]) /* eslint-disable-line */

  useEffect(()=>{
    if (props.selectedDelta) {
      const deltaTime = new Time(props.selectedDelta)
      if (defaultDeltas.includes(deltaTime.timeString)){
        setSelectedDelta(deltaTime.timeString)
      } else {
        setSelectedDelta('custom')
      }
    }
  }, [props.selectedDelta])

  const quickDateClickHandler = (e) => {
    if (defaultDeltas.includes(e.target.value)) {
      const t = new Time(e.target.value)
      setSelectedDelta(t.timeString)
      const ranges = t.getTimeRange()
      setTimes(moment.utc(ranges.startTime), moment.utc(ranges.endTime), t.timeString)
    }
  }

  const setTimes = (startTime, endTime, selectedDelta) => {
    if(startTime > endTime)
      return setErrorMessage('Oops, please ensure your start time begins before your end time')
    else
      setErrorMessage(null)
    setStartTime(startTime)
    setEndTime(endTime)
    props.setTimes(startTime, endTime, selectedDelta)
    handleCustomDateRangeClose();
  }

  const handleCustomDateRangeClose = () => {
    setErrorMessage(null)
    setCustomDateRange(false)
  }

  const handleSetCustomDateRange = () => {
    setCustomDateRange([startTime, endTime])
  }

  const selectCustomDateRange = () => {
    setTimes(customDateRange[0].utc(), customDateRange[1].utc(), 'custom')
  }

  return (
    <div className={classes.root} {...props}>
      {props.isNorthStar ?
        <div className={classes.presetContainer}>
          <div className={classes.inputContainer}>
            <p className={classes.labelText}>Time range</p>
            <Select
              id="datetime-select"
              value={selectedDelta}
              labelId="datetime-select-label"
              onChange={quickDateClickHandler}
              className={classes.quickSelectItem}
              MenuProps={{disableScrollLock: true}}
              style={{ borderRadius: 10, height: 43 }}
            >
              <MenuItem value="custom" className={classes.customSelectOption} onClick={handleSetCustomDateRange}>Custom</MenuItem>
              {deltas.map((d, i)=>(<MenuItem key={i} className={classes.quickSelectItem} value={d.timeString}>{d.language()}</MenuItem>))}
            </Select>
          </div>
        </div>
      :
        <FormControl className={classes.inlineFormControl}>
          <InputLabel id="datetime-select-label">{'Time Range'}</InputLabel>
          <Select
            MenuProps={{disableScrollLock: true}}
            labelId="datetime-select-label"
            id="datetime-select"
            value={selectedDelta}
            datacy={'datetimeSelect'}
            onChange={quickDateClickHandler}
            className={classes.quickSelectItem}
          >
            <MenuItem value="custom" onClick={handleSetCustomDateRange}>Custom</MenuItem>
            {deltas.map((d, i)=>(<MenuItem key={d.timeString} datacy={`datetimeSelectOption-${i}`} className={classes.quickSelectItem} value={d.timeString}>{d.language()}</MenuItem>))}
          </Select>
          {(!!startTime && !!endTime) && <FormHelperText datacy={`formHelperText`}>{`${startTime.local().format("l, LT")} - ${endTime.local().format("l, LT")}`}</FormHelperText>}
        </FormControl>
      }

      <Dialog disableScrollLock={true} onClose={handleCustomDateRangeClose} aria-labelledby="custom-daterange-picker" open={customDateRange}>
        <DialogTitle id="custom-daterange-picker-title">Select Date Range</DialogTitle>
        {!!errorMessage && <p className={classes.errorMessage}>{errorMessage}</p>}
        <FormGroup row>
          <div>
            <FormControl component="fieldset" className={classes.formControl}>
              <TextField
                id="date-start"
                label="Start Date"
                type="date"
                value={customDateRange?customDateRange[0].format("YYYY-MM-DD"):''}
                onChange={(event)=>{
                  let time = event.target.value
                  if(!time)
                    return
                  const [year, month, date] = time.split('-')
                  // subtract one here because months are zero indexed
                  // per moment js documentation but month extracted 
                  // from target value is not i.e. August = 7
                  let newTime = moment(customDateRange[0]).set({'year': year, 'month': month - 1, 'date': date})
                  setCustomDateRange([newTime, customDateRange[1]])
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </FormControl>
            <FormControl component="fieldset" className={classes.formControl}>
              <TextField
                id="time-start"
                label="Start Time"
                type="time"
                value={customDateRange?customDateRange[0].local().format("HH:mm"):''}
                onChange={(event)=>{
                  let time = event.target.value
                  if(!time)
                    return
                  const [hour, minute] = time.split(':')
                  let newTime = moment(customDateRange[0].local()).set('hour', hour).set('minute', minute)
                  setCustomDateRange([newTime.utc(), customDateRange[1]])
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </FormControl>
          </div>

          <div>
            <FormControl component="fieldset" className={classes.formControl}>
              <TextField
                id="date-end"
                label="End Date"
                type="date"
                value={customDateRange?customDateRange[1].format("YYYY-MM-DD"):''}
                onChange={(event)=>{
                  let time = event.target.value
                  if(!time)
                    return
                  const [year, month, date] = time.split('-')
                  // subtract one here because months are zero indexed
                  // per moment js documentation but month extracted 
                  // from target value is not i.e. August = 7
                  let newTime = moment(customDateRange[1]).set({'year': year, 'month': month - 1, 'date': date})
                  setCustomDateRange([customDateRange[0],newTime])
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </FormControl>
            <FormControl component="fieldset" className={classes.formControl}>
              <TextField
                id="time-end"
                label="End Time"
                type="time"
                value={customDateRange?customDateRange[1].format("HH:mm"):''}
                onChange={(event)=>{
                  let time = event.target.value
                  if(!time)
                    return
                  const [hour, minute] = time.split(':')
                  let newTime = moment(customDateRange[1]).set('hour', hour).set('minute', minute)
                  setCustomDateRange([customDateRange[0], newTime])
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </FormControl>
          </div>
        </FormGroup>
        <DialogActions>
          <Button onClick={handleCustomDateRangeClose} color="primary">
            Cancel
          </Button>
          <Button onClick={selectCustomDateRange} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>

    </div>
  )
}

export const TimeControls = (props) => {
  const [startTime, setStartTime] = useState()
  const [endTime, setEndTime] = useState()
  const [selectedDelta, setSelectedDelta] = useState(props.delta)


  const deltas = {
    "@:-10": null,
    "@-1": null, //"1h",
    "@-4": null, //"4h",
    "@-24": null, //"24h",
    "-7:d": null, //"7d",
    "-30:d": null, // "30d",
    "-1:w": null,
    "0:m":null,
    "-1:m":null,
    "0:q":null,
    "-1:q":null
  }
  
  useEffect(() => {
    Object.keys(props.controls).filter(uuid=>props.controls[uuid].field==='created').forEach(uuid=>{
      if(props.controls[uuid].operator === 'gt'){
        setStartTime(props.controls[uuid].value)
      }else if (props.controls[uuid].operator === 'lt'){
        setEndTime(props.controls[uuid].value)
      }else if (props.controls[uuid].operator === 'in'){
        const mappedDelta = Object.keys(deltas).find(d=>deltas[d]===props.controls[uuid].value[0])
        setStartTime()
        setEndTime()
        setSelectedDelta(mappedDelta)
        if(props.setDelta){
          props.setDelta(mappedDelta)
        }
      }
    })
  },[props.interface, props.controls]) /* eslint-disable-line */

  const setTimes = (startTime, endTime, selectedDelta) => {
    const legacyDelta = deltas[selectedDelta]
    const updated = {}
    if(legacyDelta){
      Object.keys(props.controls).filter(uuid=>props.controls[uuid].field==='created').forEach(uuid=>{
        if(props.controls[uuid].operator === 'in'){
          updated['in'] = true
          props.interface.update(uuid, {value:[legacyDelta]})
        }else{
          props.interface.remove(uuid)
        }
      })
      if(!updated['in']){
        props.interface.add({
          field:'created',
          value:[legacyDelta],
          operator:'in'
        })
      }
    } else {
      setSelectedDelta(selectedDelta)
      if(props.setDelta){
        props.setDelta(selectedDelta)
      }
      Object.keys(props.controls).filter(uuid=>props.controls[uuid].field==='created').forEach(uuid=>{
        if(props.controls[uuid].operator === 'gt'){
          updated['gt'] = true
          props.interface.update(uuid, {value:startTime.format("YYYY-MM-DDTHH:mm:ss.SSS999")})
        }else if(props.controls[uuid].operator === 'lt'){
          updated['lt'] = true
          props.interface.update(uuid, {value:endTime.format("YYYY-MM-DDTHH:mm:ss.SSS999")})
        }else{
          props.interface.remove(uuid)
        }
      })
      if(!updated['gt']){
        props.interface.add({
          field: 'created',
          value: startTime.format("YYYY-MM-DDTHH:mm:ss.SSS999"),
          operator: 'gt'
        })
      }
      if(!updated['lt']){
        props.interface.add({
          field: 'created',
          value: endTime.format("YYYY-MM-DDTHH:mm:ss.SSS999"),
          operator: 'lt'
        })
      }
    }
  }

  return (
    <TimespanPicker
      defaltDeltas = {Object.keys(deltas)}
      selectedDelta={selectedDelta}
      startTime={startTime}
      endTime={endTime}
      setTimes={setTimes}
      style={props.style}
    />
  )
}
